import { DownloadManager } from "../utils/downloadManager";
import { createPlayer } from "../utils/player/createPlayer";
import { addAudioBreadcrumb } from "../utils/player/audio/audioSentry";
import { showOfflineAccessPrompt } from "@utils/offlineAccess/prompt";
import { toast } from "sonner-native";
import { InteractionManager } from "react-native";
import { onlineManager } from "@tanstack/react-query";
import type { DefaultOptions } from "@tanstack/react-query";
import { AUTH_QUERY_KEY } from "@utils/queryKeys";
import { identifyUser } from "@utils/sentry";
import {
  queryCache,
  cachedSongs,
  cachedAlbums,
  cachedArtists,
  cachedAccesses,
  downloadQueue,
  offlineCollections,
  offlineTracks,
  offlineSongs,
  offlineAlbums,
  offlinePlaylists,
  offlineAccessLeases,
} from "@sqlite/schema";
import { openDataSession } from "../data/core/session";
import { createAuthSession } from "./authSession";
import type { DevicePorts } from "./ports";
import { resetAccount } from "./resetAccount";

export type EndSessionReason =
  | "sign-out"
  | "deleted"
  | "account-switch"
  | "restored-signed-out"
  | "preview-channel";

export function createAppRuntime(
  ports: DevicePorts,
  options: {
    queryDefaults?: DefaultOptions;
    report?: (error: unknown) => void;
  } = {},
) {
  const auth = createAuthSession(ports.auth);
  let data = openDataSession({ ...ports, auth }, options.queryDefaults);
  const player = createPlayer({
    audio: ports.audio,
    kv: ports.kv,
    resumeKv: ports.resumeKv,
    data: () => data,
    isOnline: ports.network.isOnline,
    now: () => Date.now(),
    addAudioBreadcrumb,
    observeOfflineAccess: () => data.offlineAccess.observeOfflineAccessState(),
    waitForOfflineAccessUser: () => data.offlineAccess.waitForUser(),
    showOfflineAccessPrompt,
    toast,
    afterInteractions: InteractionManager.runAfterInteractions,
  });
  const downloads = new DownloadManager({ ...ports, data: () => data });
  const lastIdentity = ports.kv.getString("data.lastIdentity");
  const mirroredUser = data.queryClient.getQueryData<{
    uid: string;
    email?: string | null;
  }>([AUTH_QUERY_KEY]);
  let identity: string | null | undefined =
    lastIdentity === undefined
      ? mirroredUser?.uid
      : (JSON.parse(lastIdentity) as string | null);
  let stopNetwork: (() => void) | undefined;
  let stopAuth: (() => void) | undefined;
  let ending: Promise<void> | undefined;
  // Set while the last account's rows could not be wiped: no session is open
  // and the app offers to try again (`retryEndSession`).
  let failedEnd: EndSessionReason | undefined;
  let disposed = false;
  const listeners = new Set<() => void>();
  const report =
    options.report ??
    ((error: unknown) => console.error("Account reset failed", error));

  // A locked or busy database usually clears in a moment: try each delete a
  // few times before asking the user (`failedEnd`).
  const retrying = async (attempt: () => unknown) => {
    for (let tries = 1; ; tries++) {
      try {
        return void (await attempt());
      } catch (error) {
        if (tries === 3 || disposed) throw error;
        await new Promise((resolve) => setTimeout(resolve, 250 * tries));
      }
    }
  };

  const endSession = (
    reason: EndSessionReason,
    generation = data.generation,
  ) => {
    if (ending) return ending;
    if (disposed || generation !== data.generation) return Promise.resolve();
    data.dispose();
    for (const listener of listeners) listener();
    data.clearAuthMirror();
    // Abort the old generation before deleting rows: clearing TanStack alone
    // cannot prevent a query function's late write-through. Unsent plays are
    // kept: they belong to their account and wait for its next sign-in.
    const clear = async () => {
      await retrying(() => ports.db.delete(queryCache).run());
      // Device cleanup (images, Intercom, analytics, download files) is best
      // effort, as it was before sessions: its failure is reported but must
      // not leave the app without a session. The rows below still go.
      await resetAccount(ports, downloads, player)
        // Restore telemetry before reporting failures or the remaining wipe,
        // which can fail or wait for a SQLite lock.
        .finally(() => identifyTelemetryUser(ports.auth.currentUser()))
        .catch(report);
      await retrying(() =>
        ports.db.transaction((tx) => {
          for (const table of [
            cachedSongs,
            cachedAlbums,
            cachedArtists,
            cachedAccesses,
            downloadQueue,
            offlineCollections,
            offlineTracks,
            offlineSongs,
            offlineAlbums,
            offlinePlaylists,
            offlineAccessLeases,
          ])
            tx.delete(table).run();
        }),
      );
    };
    ending = clear()
      .then(
        () => {
          if (disposed) return;
          failedEnd = undefined;
          data = openDataSession({ ...ports, auth }, options.queryDefaults);
          for (const listener of listeners) listener();
          // Written last: if it fails, the next cold start ends this session
          // again, which costs a cache wipe rather than the open session.
          ports.kv.set("data.lastIdentity", JSON.stringify(identity ?? null));
        },
        (error: unknown) => {
          if (!disposed) {
            failedEnd = reason;
            for (const listener of listeners) listener();
          }
          throw error;
        },
      )
      .finally(() => {
        ending = undefined;
      });
    return ending;
  };

  // Names the account on everything Sentry sends. Firebase restores its saved
  // user only after a round trip to Google, and offline that can take long
  // enough for a session's first minutes (and a shake-to-report in them) to go
  // out anonymous: the auth mirror already holds the same sign-in.
  const identifyTelemetryUser = (
    user: { uid: string; email?: string | null } | null | undefined,
  ) => {
    identifyUser(user?.uid ? { id: user.uid, email: user.email } : null);
  };

  const followIdentity = (next: string | null) => {
    const previous = identity;
    identity = next;
    if (
      (typeof previous === "string" && previous !== next) ||
      !data.isCurrent()
    ) {
      void endSession(next === null ? "sign-out" : "account-switch").catch(
        report,
      );
    } else if (!ending) {
      ports.kv.set("data.lastIdentity", JSON.stringify(next));
    }
  };

  return {
    ports,
    auth,
    player,
    downloads,
    get data() {
      return data;
    },
    subscribe(listener: () => void) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    endSession,
    /** Why the last wipe failed, while the app waits for the user to retry. */
    get failedEnd() {
      return failedEnd;
    },
    retryEndSession() {
      if (!failedEnd) return Promise.resolve();
      return endSession(failedEnd).catch(report);
    },
    start() {
      if (disposed) throw new Error("Cannot start an ended runtime");
      if (!stopNetwork) {
        onlineManager.setOnline(ports.network.isOnline());
        stopNetwork = ports.network.subscribe((online) =>
          onlineManager.setOnline(online),
        );
        // A mirror of someone other than the last identity is stale: the last
        // run ended its session.
        identifyTelemetryUser(
          ports.auth.currentUser() ??
            (mirroredUser?.uid === identity ? mirroredUser : null),
        );
        stopAuth = ports.auth.subscribe((user) => {
          identifyTelemetryUser(user);
          auth.store.setState({ firebaseUser: user });
          followIdentity(user?.uid ?? null);
        });
        const user = ports.auth.currentUser();
        if (user) followIdentity(user.uid);
      }
      return ending ?? Promise.resolve();
    },
    async dispose() {
      disposed = true;
      auth.dispose();
      player.dispose();
      stopAuth?.();
      stopNetwork?.();
      stopNetwork = undefined;
      data.dispose();
      await downloads.dispose();
      listeners.clear();
    },
  };
}

export type AppRuntime = ReturnType<typeof createAppRuntime>;
